import React, {PropsWithChildren} from "react"

import type {ButtonProps} from "@mui/material/Button"
import Button from "@mui/material/Button"
import type {CSSObject} from "@mui/material/styles"
import {styled} from "@mui/material/styles"

import type {NdComponentSizeInput} from "../../NdComponents"
import {NdComponentSize, resolveNdComponentSize} from "../../NdComponents"

/**
 * The colours a button reads in, for one state.
 *
 * @property {string} color - The text colour.
 * @property {string} bgcolor - The background colour.
 * @property {string} border - The border shorthand, `"none"` for a button that carries none.
 */
export interface NdButtonColors {
    color: string
    bgcolor: string
    border: string
}

/**
 * Everything one button style is made of: the colours it rests in, the colours it answers a pointer
 * with, the weight of its label, and the MUI variant it is drawn on top of.
 *
 * @property {ButtonProps["variant"]} variant - The MUI variant the style is drawn on.
 * @property {number} fontWeight - The weight of the label.
 * @property {NdButtonColors} rest - The colours at rest.
 * @property {Partial<NdButtonColors>} hover - What changes on hover, focus and press.
 */
export interface NdButtonTokens {
    variant: ButtonProps["variant"]
    fontWeight: number
    rest: NdButtonColors
    hover: Partial<NdButtonColors>
}

/**
 * The styles a button may read in.
 *
 * `primary` is the one call to action of a screen, `outlined` the same call stated more quietly,
 * `secondary` a supporting action, and `tertiary` a neutral one that takes the colour around it.
 */
export const NdButtonStyle = {
    primary: {
        variant: "contained",
        fontWeight: 600,
        rest: {color: "#FFFFFF !important", bgcolor: "#307ADE", border: "none"},
        hover: {bgcolor: "#21559b"}
    },
    secondary: {
        variant: "outlined",
        fontWeight: 500,
        rest: {color: "#307ADE", bgcolor: "transparent", border: "0.6px solid #307ADE4D"},
        hover: {bgcolor: "#307ADE1A"}
    },
    tertiary: {
        variant: "outlined",
        fontWeight: 500,
        rest: {color: "inherit", bgcolor: "transparent", border: "1px solid #0000001A"},
        hover: {bgcolor: "#00000005", border: "1px solid #0000004D"}
    },
    outlined: {
        variant: "outlined",
        fontWeight: 600,
        rest: {color: "#307ADE", bgcolor: "transparent", border: "1px solid #307ADE"},
        hover: {color: "#21559b", bgcolor: "#307ADE0A", border: "1px solid #21559b"}
    }
} as const satisfies Record<string, NdButtonTokens>

/** Name of a button style. */
export type NdButtonStyleName = keyof typeof NdButtonStyle

/**
 * The room a button takes and how loudly it reads.
 *
 * @property {string} fontSize - The size of the label.
 * @property {string} padding - The room around the label.
 * @property {string} borderRadius - How far the corners are rounded.
 * @property {string} iconFontSize - The size of a leading glyph.
 */
export interface NdButtonSizeTokens {
    fontSize: string
    padding: string
    borderRadius: string
    iconFontSize: string
}

/**
 * The sizes a button may be drawn at, one entry per house size.
 */
export const NdButtonSize = {
    [NdComponentSize.SMALL]: {fontSize: "x-small", padding: "4px 5px", borderRadius: "4px", iconFontSize: "12px"},
    [NdComponentSize.MEDIUM]: {fontSize: "0.875rem", padding: "8px 12px", borderRadius: "8px", iconFontSize: "12px"},
    [NdComponentSize.LARGE]: {fontSize: "1rem", padding: "10px 16px", borderRadius: "12px", iconFontSize: "16px"}
} as const satisfies Record<NdComponentSize, NdButtonSizeTokens>

/**
 * Props for the {@link NdButton} component.
 *
 * Everything a MUI `Button` takes is taken here too, save `variant`, which the button style names on
 * its own so a call site cannot pair a style with a variant it was not drawn for.
 *
 * @property {NdButtonStyleName} buttonstyle - The style the button reads in.
 * @property {NdComponentSizeInput} [size="medium"] - The size it is drawn at, spelled in full or as its short alias.
 */
export interface NdButtonProps extends Omit<ButtonProps, "variant" | "size">, PropsWithChildren {
    buttonstyle: NdButtonStyleName
    size?: NdComponentSizeInput
}

/**
 * Turns one set of state colours into the rules that paint them.
 *
 * @param {Partial<NdButtonColors>} colors - The colours of a state, each one optional.
 * @returns {CSSObject} The rules to apply in that state.
 */
const paint = ({color, bgcolor, border}: Partial<NdButtonColors>): CSSObject => ({
    ...(color !== undefined && {color}),
    ...(bgcolor !== undefined && {backgroundColor: bgcolor}),
    ...(border !== undefined && {border})
})

/**
 * Builds the rules of one button style, at one size.
 *
 * The style rules are hung off the MUI variant class the style is drawn on, and the size rules off
 * the MUI size class, so a call site keeps its say: an `sx` written against the same class is
 * inserted after these and therefore wins.
 *
 * @param {NdButtonStyleName} buttonstyle - The style the button reads in.
 * @param {NdComponentSize} size - The size it is drawn at, under its full name.
 * @returns {CSSObject} The rules to apply to the button.
 */
const buttonSx = (buttonstyle: NdButtonStyleName, size: NdComponentSize): CSSObject => {
    const {variant, fontWeight, rest, hover} = NdButtonStyle[buttonstyle]
    const {fontSize, padding, borderRadius, iconFontSize} = NdButtonSize[size]

    const variantClass = `&.MuiButton-${variant}`
    const sizeClass = `&.MuiButton-size${size.charAt(0).toUpperCase()}${size.slice(1)}`

    return {
        textTransform: "none",
        boxShadow: "none",
        borderRadius,
        padding,
        ".MuiButton-startIcon:has(+ *:not(.MuiTouchRipple-root))": {
            marginRight: 0,
            paddingRight: "6px",
            paddingLeft: "3px"
        },
        ".MuiButton-startIcon>*:nth-of-type(1)": {fontSize: iconFontSize},
        [sizeClass]: {fontSize, padding, borderRadius},
        [variantClass]: {
            fontWeight,
            ...paint(rest),
            ":hover, :focus, :active": paint(hover),
            "&.Mui-disabled": {opacity: "50%"}
        }
    }
}

/** What the styled button underneath takes: a house size already read under its full name. */
type NdStyledButtonProps = Omit<ButtonProps, "size"> & {buttonstyle: NdButtonStyleName; size?: NdComponentSize}

const NdStyledButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== "buttonstyle"
})<NdStyledButtonProps>(({buttonstyle, size = NdComponentSize.MEDIUM}) => buttonSx(buttonstyle, size))

/**
 * House button: a MUI `Button` painted from the style and size tokens above.
 *
 * @param {Readonly<NdButtonProps>} props - The props for the component.
 * @returns {JSX.Element} The rendered button.
 */
export const NdButton = ({
    buttonstyle,
    size = NdComponentSize.MEDIUM,
    children,
    ...props
}: Readonly<NdButtonProps>): JSX.Element => (
    <NdStyledButton
        buttonstyle={buttonstyle}
        size={resolveNdComponentSize(size)}
        variant={NdButtonStyle[buttonstyle].variant}
        {...props}
    >
        {children}
    </NdStyledButton>
)

export default NdButton
